import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import type { IFormElement } from '../types';
import { useFormState } from '../contexts/FormStateContext';
import styles from './fields.module.css';

export interface TextFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

export const TextField: React.FC<TextFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const formState = useFormState();
  const error = errors[element.name];
  const validationError = formState.validationErrors[element.name];

  const getInputType = () => {
    switch (element.inputType) {
      case 'email':
        return 'email';
      case 'tel':
        return 'tel';
      case 'url':
        return 'url';
      case 'password':
        return 'password';
      default:
        return 'text';
    }
  };

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
        htmlFor={element.name}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <Controller
        name={element.name}
        control={control}
        defaultValue={element.defaultValue || ''}
        render={({ field }) => (
          <input
            {...field}
            id={element.name}
            type={getInputType()}
            className={`${styles.input} ${
              error || validationError ? styles.error : ''
            }`}
            placeholder={element.placeholder || ''}
            disabled={element.readOnly}
            maxLength={element.maxLength}
            autoComplete="off"
            value={field.value || ''}
            onChange={(e) => {
              e.stopPropagation();
              field.onChange(e.target.value);
              if (validationError) {
                formState.clearValidationError(element.name);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            onBlur={(e) => {
              e.stopPropagation();
              field.onBlur();
              formState.markFieldAsTouched(element.name);
            }}
          />
        )}
      />
      {(error || validationError) && (
        <p className={styles.errorMessage}>
          {(error?.message as string) || validationError}
        </p>
      )}
    </div>
  );
};

export default TextField;
